/**
 * Relationship Manager resolution + auto-acknowledgement suppression.
 *
 * A Relationship Manager (RM) is stored as a handle or an email
 * (contacts.relationship_manager, properties.relationship_manager); normalizeRm
 * maps it to a team address, and DEFAULT_RM_EMAIL (Ryan) stands in when there is
 * none. The intake receipt no longer CCs an RM: since email intake v2 it goes
 * through external-ack.ts to the one external address, and the RM sees the report.
 *
 * Pure — no Deno globals — so it's unit-tested by vitest and imported by edge
 * functions and shared modules alike.
 */

// The four people who can own a broker relationship. Keyed by a short handle so
// a stored value of either the handle ("frank") or the full email resolves.
export const TEAM_RMS: Record<string, string> = {
  ryan: 'ryan@elevationcg.com',
  frank: 'frank@elevationcg.com',
  chad: 'chad@elevationcg.com',
  jamie: 'jamie@elevationcg.com',
};

/** Fallback RM when the sender has no contact / no RM assigned. */
export const DEFAULT_RM_EMAIL = 'ryan@elevationcg.com';

/** Internal domains — email FROM one of these is us/the team, never a broker. */
const INTERNAL_DOMAINS = ['elevationcg.com', 'endeavorcommunities.com'];

/**
 * True when the address belongs to us. Used to keep the FORWARDER's signature
 * (captured by the email-body extractor as "the broker") out of deal contacts.
 */
export function isInternalEmail(email: string | null | undefined): boolean {
  const m = (email ?? '').trim().toLowerCase().match(/@([^@\s]+)$/);
  if (!m) return false;
  return INTERNAL_DOMAINS.some((d) => m[1] === d || m[1].endsWith('.' + d));
}

/**
 * Normalize a stored relationship_manager value (handle or email, any case) to a
 * known team email, or null if it isn't one of the four. Prevents a typo/stale
 * value from CC'ing a stranger.
 */
export function normalizeRm(value: string | null | undefined): string | null {
  const v = (value ?? '').trim().toLowerCase();
  if (!v) return null;
  if (TEAM_RMS[v]) return TEAM_RMS[v];
  if (Object.values(TEAM_RMS).includes(v)) return v;
  return null;
}

const NOREPLY_RE =
  /^(no[-_.]?reply|do[-_.]?not[-_.]?reply|donotreply|mailer[-_.]?daemon|postmaster|bounce[s]?|abuse|notifications?|automated|auto[-_.]?reply|noreply)([-+.].*)?@/i;

/** Resend accepts 50 recipients total; leave room for the To + a margin. */
const REPORT_CC_CAP = 10;

/**
 * Collapse plus-addressing and case for COMPARISON (never for sending):
 * "Flow+deal@Elevationcg.com" → "flow@elevationcg.com". Exchange Online has
 * plus addressing on by default, so an alias check on exact strings lets
 * flow+x@ slip past and feeds every report back into the polled inbox.
 */
export function comparableAddress(s: string | null | undefined): string {
  const email = (s ?? '').trim().toLowerCase();
  const m = email.match(/^([^@+]+)(\+[^@]*)?@(.+)$/);
  return m ? `${m[1]}@${m[3]}` : email;
}

/**
 * Filter the job's captured CC list into what an ANALYSIS REPORT may CC.
 *
 * The analysis is INTERNAL underwriting — a pursue/pass verdict, the firm's
 * expense benchmarks, and prose about the seller's numbers. The captured list
 * is whoever was on the source email, and the classic reply-all slip leaves
 * the BROKER on it. So the filter is an allowlist, not a blocklist: only
 * addresses on our own domains pass (which is also why this deliberately does
 * NOT reuse shouldAcknowledge/isInternalEmail — their internal-domain rule
 * exists to suppress acks TO teammates, the exact audience this list is for;
 * jamie@endeavorcommunities.com is the address this feature exists for).
 * Additionally dropped: malformed and no-reply/daemon addresses, Flow's own
 * aliases and sending identity (plus-addressing collapsed, or the report
 * re-enters the poller as inbound mail), the To recipient, and everything
 * past a sane cap.
 */
const REPORT_CC_ALLOWED_DOMAINS = ['elevationcg.com', 'endeavorcommunities.com'];

export function filterReportCc(
  cc: Array<string | null | undefined> | null | undefined,
  to: string | null | undefined,
  blocked: Array<string | null | undefined> = [],
): string[] {
  const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();
  const toCmp = comparableAddress(to);
  const blockedSet = new Set(blocked.map(comparableAddress).filter(Boolean));
  const out: string[] = [];
  for (const raw of cc ?? []) {
    const email = norm(raw);
    const cmp = comparableAddress(email);
    if (!email || cmp === toCmp || blockedSet.has(cmp)) continue;
    const m = email.match(/^[^@\s]+@([^@\s]+\.[^@\s]+)$/);
    if (!m) continue;
    const domain = m[1];
    if (!REPORT_CC_ALLOWED_DOMAINS.some((d) => domain === d || domain.endsWith('.' + d))) continue;
    if (NOREPLY_RE.test(email)) continue;
    if (!out.includes(email)) out.push(email);
    if (out.length >= REPORT_CC_CAP) break;
  }
  return out;
}

/**
 * Decide whether to auto-acknowledge an inbound sender. Suppresses the classic
 * ways an auto-responder embarrasses itself: missing/garbage addresses,
 * no-reply/bounce/daemon senders, and our own team (internal domains) — acking
 * a teammate who forwarded a deal, or acking ourselves, would loop.
 */
export function shouldAcknowledge(
  senderEmail: string | null | undefined,
  opts: { internalDomains?: string[] } = {},
): { ok: boolean; reason?: string } {
  const email = (senderEmail ?? '').trim().toLowerCase();
  if (!email) return { ok: false, reason: 'no sender address' };

  // Must look like a real deliverable address (local@domain.tld).
  const m = email.match(/^[^@\s]+@([^@\s]+\.[^@\s]+)$/);
  if (!m) return { ok: false, reason: 'not a valid email address' };
  const domain = m[1];

  if (NOREPLY_RE.test(email)) return { ok: false, reason: 'no-reply / automated sender' };

  const internal = opts.internalDomains ?? INTERNAL_DOMAINS;
  if (internal.some((d) => domain === d || domain.endsWith('.' + d))) {
    return { ok: false, reason: 'internal / team address' };
  }

  return { ok: true };
}
